import { Box, Typography, Card, Avatar, Chip, Stack, Drawer, CardActions, lighten, useTheme } from '@mui/material';
import { useGetAgencyAgentTemplates } from 'apis/agency/agentTemplate/hook';
import { AgentTemplateResponse } from 'apis/agency/agentTemplate/types';
import Pagination, { usePagination } from 'components/Common/Pagination';
import { useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { titleCase } from 'utils/helperFunctions';
import Grid from '@mui/material/Grid2';
import Button from '@/components/Common/Button';
import { IoMdPause, IoMdPlay } from 'react-icons/io';
import LinkIcon from '@/assets/icons/LinkIcon';
import { useGetAvailableIntegrations } from '@/apis/agency/integrations/hook';
import { EmptyStateView } from '@/components/Common/EmptyStateView';
import AgentTemplateSkeleton from '../../AgentTemplate/components/AgentTemplateSkeleton';
import AgentDetailsDrawer from './AgentDetailsDrawer';

interface DefaultTemplateProps {
  integration?: string;
}

export default function DefaultTemplate({ integration }: DefaultTemplateProps) {
  const { currentPage, perPage, setPage, setPerPage } = usePagination();
  const [selectedTemplate, setSelectedTemplate] = useState<AgentTemplateResponse | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [playingTemplateId, setPlayingTemplateId] = useState<any>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { data: agentTemplates, isLoading: isAgentTemplateLoading } = useGetAgencyAgentTemplates(
    currentPage,
    integration,
    perPage
  );

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedTemplate(null);
  };

  const handleAudioPlay = (templateId: number, recordingUrl: string) => {
    if (playingTemplateId === templateId && audioRef.current) {
      audioRef.current.pause();
      setPlayingTemplateId(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    const audio = new Audio(recordingUrl);
    audio.addEventListener('ended', () => setPlayingTemplateId(null));
    audio.play();
    audioRef.current = audio;
    setPlayingTemplateId(templateId);
  };

  const filteredTemplates = agentTemplates?.data || [];

  return (
    <>
      <Box
        display='flex'
        flexDirection={{ xs: 'column', lg: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        gap={{ xs: 2, lg: 0 }}
        mb={3}
      >
        <Typography variant='h6' fontWeight={500}>
          Default Templates
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {isAgentTemplateLoading ? (
          <>
            {Array.from({ length: 6 }).map((_, index) => (
              <Grid size={{ sm: 6, md: 4 }} key={index}>
                <AgentTemplateSkeleton />
              </Grid>
            ))}
          </>
        ) : filteredTemplates.length === 0 ? (
          <Grid size={12} mt={3}>
            <EmptyStateView />
          </Grid>
        ) : (
          filteredTemplates.map((template) => (
            <Grid size={{ sm: 6, md: 4 }} key={template.id}>
              <AgentTemplateCard
                template={template}
                isPlaying={playingTemplateId === template.id}
                onPlay={handleAudioPlay}
                onReadMore={() => {
                  setSelectedTemplate(template);
                  setDrawerOpen(true);
                }}
              />
            </Grid>
          ))
        )}
      </Grid>

      <Box mt={3}>
        <Pagination
          totalPages={agentTemplates?.meta?.total_pages}
          totalCount={agentTemplates?.meta?.total_count ?? 0}
          currentPage={currentPage}
          perPage={perPage}
          onChange={setPage}
          onPerPageChange={setPerPage}
        />
      </Box>

      <Drawer
        anchor='right'
        open={drawerOpen}
        onClose={handleDrawerClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 800,
            maxWidth: '90vw'
          }
        }}
      >
        <AgentDetailsDrawer template={selectedTemplate} onClose={handleDrawerClose} />
      </Drawer>
      <Outlet />
    </>
  );
}

interface AgentTemplateCardProps {
  template: AgentTemplateResponse;
  isPlaying: boolean;
  onPlay: (templateId: number, recordingUrl: string) => void;
  onReadMore: (template: AgentTemplateResponse) => void;
}

const AgentTemplateCard = ({ template, isPlaying, onPlay, onReadMore }: AgentTemplateCardProps) => {
  const theme = useTheme();
  const { data: availableIntegrations } = useGetAvailableIntegrations({
    per_page: 999
  });

  const getAvatarUrl = (code: string) => {
    return availableIntegrations?.data?.find((i) => i.code === code)?.avatar_url;
  };

  const handlePlay = () => {
    if (!template.recording_url) return;
    onPlay(template.id, template.recording_url);
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid #ECECEC',
        boxShadow: 'none'
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <Box display='flex' alignItems='center' justifyContent='space-between' mb={2} paddingX={2} paddingTop={2}>
          <Box display='flex' alignItems='center' gap={1}>
            <Avatar src={template.avatar.url || undefined} sx={{ width: 40, height: 40 }}>
              {template.agent_name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box display='flex' gap={1}>
              <LinkIcon />
              <img
                src={getAvatarUrl(template.integrations?.[0]?.code) || ''}
                style={{ height: '30px', width: '100px', objectFit: 'contain' }}
                alt={template.industry}
              />
            </Box>
          </Box>
        </Box>

        <Typography fontSize={20} fontWeight={600} mb={1} paddingX={2}>
          {template.name}
        </Typography>

        <Typography color='text.secondary' fontSize={14} mb={2} sx={{ minHeight: 40 }} paddingX={2}>
          {template.about}
        </Typography>

        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Stack direction='row' flexWrap='wrap' gap={0.5} mb={2} paddingX={2}>
            {[template.industry, template.call_direction, ...template.tags].map(
              (tag: string, idx: number) =>
                tag && (
                  <Chip
                    key={`${tag}-${idx}`}
                    label={titleCase(tag)}
                    size='small'
                    sx={{
                      fontSize: '0.75rem',
                      height: 32,
                      bgcolor: '#FAFAFA',
                      border: '1px solid #e7e4e4ff',
                      paddingX: 1,
                      borderRadius: '100px',
                      fontWeight: 400
                    }}
                  />
                )
            )}
          </Stack>

          <CardActions
            sx={{
              bgcolor: lighten(theme.palette.primary.light, 0.95),
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderTop: '1px solid #F9FAFB'
            }}
          >
            <Button
              variant='text'
              size='small'
              onClick={() => onReadMore(template)}
              sx={{ textTransform: 'none', fontWeight: 500, color: '#777777' }}
            >
              Read More
            </Button>
            <Button
              variant='outlined'
              size='small'
              startIcon={isPlaying ? <IoMdPause size={16} /> : <IoMdPlay size={16} />}
              onClick={handlePlay}
              sx={{ minWidth: 'auto', px: 2, bgcolor: '#fff' }}
            >
              {isPlaying ? 'Pause' : 'Play'}
            </Button>
          </CardActions>
        </Box>
      </Box>
    </Card>
  );
};
